import React, { useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import ApiURL from '../Api/Api';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { DEFAULT_COMPANY_THEME, compressLogoFile, resolveLogoUrl, storeCompanyBranding } from '../utils/companyTheme';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { SidebarContext } from './SidebarContext';

const money = (value) => `Rs. ${Number(value || 0).toLocaleString('en-IN')}`;

const CompanyPlanProfile = () => {
  const companyId = localStorage.getItem('company_id') || '';
  const userId = localStorage.getItem('id') || '';
  const { isSidebarOpen } = useContext(SidebarContext);
  const [plan, setPlan] = useState(null);
  const [usage, setUsage] = useState(null);
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [branding, setBranding] = useState({
    theme_color: localStorage.getItem('company_theme_color') || DEFAULT_COMPANY_THEME,
    logo_data_url: '',
    logo_path: localStorage.getItem('company_logo_path') || '',
  });
  const [brandingLogoName, setBrandingLogoName] = useState('');
  const [isBrandingSaving, setIsBrandingSaving] = useState(false);

  const selectedPackage = useMemo(
    () => packages.find((pkg) => String(pkg.id) === String(selectedPackageId)),
    [packages, selectedPackageId]
  );

  const loadData = async () => {
    try {
      const [usageResponse, packagesResponse] = await Promise.all([
        fetch(`${ApiURL}/getCompanyPackageUsage.php?company_id=${encodeURIComponent(companyId)}`),
        fetch(`${ApiURL}/get_active_packages.php`),
      ]);
      const usageData = await usageResponse.json();
      const packageData = await packagesResponse.json();
      if (usageData.status !== 'success') throw new Error(usageData.message || 'Failed to load plan');
      if (packageData.status !== 'success') throw new Error(packageData.message || 'Failed to load plans');
      setPlan(usageData.plan || null);
      setUsage(usageData.usage || null);
      setPackages(packageData.packages || []);
      if (usageData.plan) {
        const nextBranding = {
          company_name: usageData.plan.company_name || '',
          theme_color: usageData.plan.theme_color || DEFAULT_COMPANY_THEME,
          logo_path: usageData.plan.logo_path || '',
          logo_data_url: '',
        };
        setBranding(nextBranding);
        storeCompanyBranding(nextBranding);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to load plan profile');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const changePlan = async () => {
    if (!selectedPackageId) {
      toast.error('Please select a plan');
      return;
    }
    setIsSaving(true);
    try {
      const response = await fetch(`${ApiURL}/companyChangePlan.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, package_id: Number(selectedPackageId) }),
      });
      const data = await response.json();
      if (data.status !== 'success') throw new Error(data.message || 'Failed to change plan');
      setPlan(data.plan || null);
      setUsage(data.usage || null);
      localStorage.setItem('package_info', JSON.stringify(data.plan || null));
      localStorage.setItem('package_usage', JSON.stringify(data.usage || null));
      setSelectedPackageId('');
      toast.success(data.message || 'Plan changed');
    } catch (error) {
      toast.error(error.message || 'Failed to change plan');
    } finally {
      setIsSaving(false);
    }
  };

  const changeLogo = async (event) => {
    const file = event.target.files?.[0];
    try {
      const compressed = await compressLogoFile(file);
      setBranding((prev) => ({ ...prev, logo_data_url: compressed }));
      setBrandingLogoName(file.name);
    } catch (error) {
      toast.error(error.message || 'Unable to process logo');
    }
  };

  const saveBranding = async () => {
    setIsBrandingSaving(true);
    try {
      const response = await fetch(`${ApiURL}/updateCompanyBranding.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          theme_color: branding.theme_color,
          logo_data_url: branding.logo_data_url,
        }),
      });
      const data = await response.json();
      if (data.status !== 'success') throw new Error(data.message || 'Failed to update branding');
      setPlan(data.package_info || plan);
      localStorage.setItem('package_info', JSON.stringify(data.package_info || null));
      storeCompanyBranding(data.company_branding || data.package_info || {});
      setBranding((prev) => ({
        ...prev,
        logo_data_url: '',
        logo_path: data.company_branding?.logo_path || data.package_info?.logo_path || prev.logo_path,
      }));
      setBrandingLogoName('');
      toast.success(data.message || 'Branding updated');
    } catch (error) {
      toast.error(error.message || 'Failed to update branding');
    } finally {
      setIsBrandingSaving(false);
    }
  };

  return (
    <div className="app-shell min-h-screen pt-16">
      <Navbar />
      <Sidebar />
      <main className={`min-w-0 px-4 py-6 transition-all duration-300 ${isSidebarOpen ? 'lg:ml-72' : ''}`}>
      <div className="mx-auto max-w-6xl space-y-5">
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">Company Profile</p>
          <h1 className="mt-2 text-2xl font-black text-slate-900">Plan Management</h1>
          <p className="mt-2 text-sm text-slate-500">
            Change the active plan directly. Your previous plan will become inactive and the selected plan will become active from today.
          </p>
        </section>

        <div className="grid gap-5 lg:grid-cols-3">
          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-1">
            <h2 className="text-lg font-black text-slate-900">Current Plan</h2>
            <div className="mt-4 space-y-3 text-sm">
              <div>
                <div className="text-slate-500">Plan</div>
                <div className="text-xl font-black text-slate-900">{plan?.package_name || '-'}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-slate-500">Admins</div><div className="font-bold">{usage?.admin_count || 0} / {plan?.max_admins || 0}</div></div>
                <div><div className="text-slate-500">Workers</div><div className="font-bold">{usage?.worker_count || 0} / {plan?.max_workers || 0}</div></div>
                <div><div className="text-slate-500">Users</div><div className="font-bold">{usage?.total_users || 0} / {plan?.max_users || 0}</div></div>
                <div><div className="text-slate-500">Valid Till</div><div className="font-bold">{plan?.subscription_end_date || '-'}</div></div>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">Company Branding</p>
                <h2 className="mt-1 text-lg font-black text-slate-900">Logo and Theme Color</h2>
                <p className="mt-1 text-sm text-slate-500">This logo and color will be visible to every admin and worker in your company.</p>
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="h-11 w-11 overflow-hidden rounded-lg border border-slate-200 bg-white">
                  {branding.logo_data_url || branding.logo_path ? (
                    <img src={branding.logo_data_url || resolveLogoUrl(branding.logo_path)} alt="Company Logo" className="h-full w-full object-contain p-1" />
                  ) : (
                    <BuildingOfficeIcon className="m-2.5 h-6 w-6 text-slate-400" />
                  )}
                </div>
                <div>
                  <div className="text-sm font-black text-slate-900">{plan?.company_name || 'Company'}</div>
                  <div className="text-xs text-slate-500">{branding.theme_color?.toUpperCase()}</div>
                </div>
              </div>
            </div>

            <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_260px]">
              <label className="flex min-h-[96px] cursor-pointer items-center gap-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 transition hover:border-indigo-300 hover:bg-white">
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={changeLogo} />
                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white">
                  {branding.logo_data_url || branding.logo_path ? (
                    <img src={branding.logo_data_url || resolveLogoUrl(branding.logo_path)} alt="Company Logo Preview" className="h-full w-full object-contain p-1" />
                  ) : (
                    <BuildingOfficeIcon className="h-6 w-6 text-slate-400" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-800">{brandingLogoName || 'Change company logo'}</p>
                  <p className="mt-1 text-xs text-slate-500">Upload any PNG, JPG, or WEBP. It will be compressed automatically.</p>
                </div>
              </label>

              <div className="rounded-xl border border-slate-200 bg-white p-3">
                <label className="block text-sm font-medium text-slate-700 mb-2">Theme color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={branding.theme_color || DEFAULT_COMPANY_THEME}
                    onChange={(event) => setBranding((prev) => ({ ...prev, theme_color: event.target.value }))}
                    className="h-12 w-14 cursor-pointer rounded-lg border border-slate-200 bg-white p-1"
                  />
                  <span className="text-sm font-bold text-slate-800">{(branding.theme_color || DEFAULT_COMPANY_THEME).toUpperCase()}</span>
                </div>
                <div className="mt-3 flex gap-2">
                  {['#0f66bb', '#059669', '#7c3aed', '#dc2626', '#d97706'].map((color) => (
                    <button
                      type="button"
                      key={color}
                      onClick={() => setBranding((prev) => ({ ...prev, theme_color: color }))}
                      className={`h-7 w-7 rounded-full border-2 ${branding.theme_color === color ? 'border-slate-900' : 'border-white'} shadow-sm`}
                      style={{ backgroundColor: color }}
                      aria-label={`Select ${color}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={saveBranding}
              disabled={isBrandingSaving}
              className="mt-5 rounded-lg bg-indigo-600 px-5 py-3 text-sm font-bold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {isBrandingSaving ? 'Saving...' : 'Save Branding'}
            </button>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
            <h2 className="text-lg font-black text-slate-900">Select New Plan</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {packages.map((pkg) => {
                const selected = String(selectedPackageId) === String(pkg.id);
                const current = String(plan?.package_id) === String(pkg.id);
                return (
                  <button
                    key={pkg.id}
                    type="button"
                    onClick={() => setSelectedPackageId(pkg.id)}
                    className={`rounded-xl border p-4 text-left transition ${selected ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white hover:border-indigo-200'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-black text-slate-900">{pkg.package_name}</div>
                        <div className="mt-1 text-sm text-slate-500">{money(pkg.price)} / {pkg.duration_months} months</div>
                      </div>
                      {current && <span className="rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-bold text-emerald-700">CURRENT</span>}
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-slate-600">
                      <span>Admins: <b>{pkg.max_admins}</b></span>
                      <span>Workers: <b>{pkg.max_workers}</b></span>
                      <span>Total: <b>{pkg.max_users}</b></span>
                    </div>
                  </button>
                );
              })}
            </div>

            {selectedPackage && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Last plan <b>{plan?.package_name || '-'}</b> will become inactive. New plan <b>{selectedPackage.package_name}</b> will become active from today.
              </div>
            )}

            <button
              type="button"
              onClick={changePlan}
              disabled={isSaving || !selectedPackageId}
              className="mt-4 rounded-lg bg-indigo-600 px-5 py-3 text-sm font-bold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {isSaving ? 'Updating...' : 'Activate Selected Plan'}
            </button>
          </section>
        </div>
      </div>
      </main>
    </div>
  );
};

export default CompanyPlanProfile;
